import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getReleaseDetails } from "../../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../../helpers/aws_api_helper";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";
import RemediationPreviewPanel, {
  REMEDIATION_PREVIEW_PANEL_WIDTH_PX,
} from "../../../components/Common/RemediationPreviewPanel";

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const OUTLINED_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: "#64748b",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#111827",
  },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#fff4eb",
      },
    },
  },
};

const INFO_ALERT_SX = {
  fontSize: "0.8125rem",
  borderRadius: "8px",
  backgroundColor: "#eff6ff",
  color: "#1e40af",
  border: "1px solid #bfdbfe",
  "& .MuiAlert-icon": { color: "#2563eb" },
  "& .MuiAlert-message": { width: "100%" },
};

const GREY_CLOSE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  color: "#fff",
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#5a6268",
    borderColor: "#5a6268",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#adb5bd",
    borderColor: "#adb5bd",
    color: "#fff",
  },
};

const PRIMARY_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  backgroundColor: BRAND_ORANGE,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: BRAND_ORANGE_HOVER,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

/** Matches Pair Account “Click to Launch” (#1976d2). */
const LAUNCH_BLUE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  backgroundColor: "#1976d2",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1565c0",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const DIALOG_PAPER_SX = {
  borderRadius: "12px",
  m: 2,
  maxHeight: "90dvh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  ...ACCOUNT_DETAIL_FONT_SX,
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Same `fileName` as Configuration → Add-ons → FinOps (Cost Insight) manual configuration
 * (`getReleaseDetails` / `GET cloudnova/documentation?fileName=...`).
 */
const getFinOpsManualGuideFileName = () => {
  const fromEnv = process.env.REACT_APP_COST_INSIGHT_MANUAL_CONFIGURATION_GUIDE;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim();
  }
  return "configurations/Cost_Insight_Manual_Guide.pdf";
};

/** Manual panel max width (right) — matches Edit Account remediation / guide panel. */
const OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX = REMEDIATION_PREVIEW_PANEL_WIDTH_PX;
const OFFLINE_CUR_DIALOG_MR_WITH_MANUAL = `min(${24 + OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX}px, calc(40vw + 24px))`;
/** Above MUI Dialog (1300) so Confirm stays clickable over the manual panel. */
const OFFLINE_CUR_MANUAL_Z_INDEX = 1400;
const OFFLINE_CUR_CONFIRM_Z_INDEX = 1500;

const PREVIEW_PANEL_COLORS = {
  card: "#ffffff",
  border: "#e4e4e7",
  borderLight: "#f4f4f5",
  text: "#18181b",
  textMuted: "#71717a",
  background: "#fafafa",
};

/** `GET cloudnova/documentation` may return the signed URL in `url`, `signedUrl`, or other keys. */
const isHttpUrlString = (v) => typeof v === "string" && /^https?:\/\//i.test(v.trim());

const pickDocumentationUrlFromReleaseResponse = (response) => {
  if (response == null) return "";
  const status = Number(response.status);
  if (Number.isNaN(status) || status !== 200) return "";
  const named = [
    response.url,
    response.signedUrl,
    response.documentUrl,
    response.fileUrl,
    response.link,
    response.href,
  ];
  for (const v of named) {
    if (isHttpUrlString(v)) return v.trim();
  }
  const skip = new Set(["status", "message", "error", "data", "isSynthetic"]);
  if (response && typeof response === "object") {
    for (const [k, v] of Object.entries(response)) {
      if (skip.has(k) || v == null) continue;
      if (isHttpUrlString(v)) return String(v).trim();
    }
  }
  return "";
};

const UploadOfflineCurModal = ({
  open,
  onClose,
  onSubmit,
  isUploading,
  uploadProgress,
  onRequestCancelUpload,
  accountNumbers = [],
}) => {
  const now = new Date();
  const [accountNumber, setAccountNumber] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formError, setFormError] = useState("");
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [manualPanelOpen, setManualPanelOpen] = useState(true);

  const [finOpsManualGuideUrl, setFinOpsManualGuideUrl] = useState("");
  const [finOpsManualGuideLoading, setFinOpsManualGuideLoading] = useState(false);
  const [finOpsManualGuideError, setFinOpsManualGuideError] = useState("");
  /** Alias: API-fetched document URL (some bundles/hot-reloads may still reference the old name). */
  const finOpsManualGuideSrc = finOpsManualGuideUrl;

  useEffect(() => {
    if (!open) {
      setFinOpsManualGuideUrl("");
      setFinOpsManualGuideLoading(false);
      setFinOpsManualGuideError("");
      setUploadConfirmOpen(false);
      setManualPanelOpen(true);
      return;
    }
    setManualPanelOpen(true);

    let cancelled = false;
    const fileName = getFinOpsManualGuideFileName();

    (async () => {
      setFinOpsManualGuideLoading(true);
      setFinOpsManualGuideError("");
      setFinOpsManualGuideUrl("");
      try {
        const response = await getReleaseDetails(fileName);
        if (cancelled) return;
        const docUrl = pickDocumentationUrlFromReleaseResponse(response);
        if (docUrl) {
          setFinOpsManualGuideUrl(docUrl);
        } else {
          setFinOpsManualGuideError(
            getApiErrorMessage(response) || "Could not load the FinOps manual document."
          );
        }
      } catch (e) {
        if (!cancelled) {
          setFinOpsManualGuideError(
            getApiErrorMessage(e?.data ?? e) || e?.message || "Could not load the FinOps manual document."
          );
        }
      } finally {
        if (!cancelled) setFinOpsManualGuideLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];
  }, []);

  const accountNumberOptions = useMemo(() => {
    if (!Array.isArray(accountNumbers)) return [];
    const seen = new Set();
    const list = [];
    accountNumbers.forEach((x) => {
      const s = x != null ? String(x).trim() : "";
      if (s && !seen.has(s)) {
        seen.add(s);
        list.push(s);
      }
    });
    return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [accountNumbers]);

  const normalizedAccountNumber = accountNumber.trim();
  const isValidAccountNumber = /^\d{12}$/.test(normalizedAccountNumber);

  const organizationDisplayName = useMemo(() => {
    try {
      const raw = localStorage.getItem("selectedOrganization");
      if (!raw) return "";
      const o = JSON.parse(raw);
      const n = o?.orgName ?? o?.organizationName ?? o?.org_name;
      return n != null ? String(n).trim() : "";
    } catch {
      return "";
    }
  }, [open]);

  const isFormComplete =
    isValidAccountNumber && Boolean(year) && Boolean(month) && selectedFiles.length > 0;

  const handleFileChange = (event) => {
    const list = Array.from(event.target.files || []);
    if (list.length === 0) {
      setSelectedFiles([]);
      if (event.target) event.target.value = "";
      return;
    }
    const valid = list.filter((f) => f.name.toLowerCase().endsWith(".gz"));
    if (valid.length === 0) {
      setFormError("Only gzip files (.gz) are supported.");
      setSelectedFiles([]);
    } else {
      if (valid.length < list.length) {
        setFormError(`${list.length - valid.length} file(s) skipped (not .gz).`);
      } else {
        setFormError("");
      }
      setSelectedFiles(valid);
    }
    if (event.target) event.target.value = "";
  };

  const resetModalState = () => {
    setAccountNumber("");
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedFiles([]);
    setFormError("");
  };

  const finishClose = () => {
    resetModalState();
    onClose();
  };

  const handleClose = () => {
    if (isUploading) {
      onRequestCancelUpload?.();
      return;
    }
    finishClose();
  };

  const validateBeforeUpload = () => {
    if (!normalizedAccountNumber) {
      setFormError("Account number is required.");
      return false;
    }
    if (!isValidAccountNumber) {
      setFormError("Account number must be exactly 12 digits.");
      return false;
    }
    if (selectedFiles.length === 0) {
      setFormError("Please select at least one file to upload.");
      return false;
    }
    if (selectedFiles.some((f) => !f.name.toLowerCase().endsWith(".gz"))) {
      setFormError("Only gzip files (.gz) are supported.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleUploadClick = () => {
    if (!validateBeforeUpload()) return;
    setUploadConfirmOpen(true);
  };

  const handleConfirmUpload = async () => {
    setUploadConfirmOpen(false);
    setFormError("");
    const success = await onSubmit({
      accountNumber: normalizedAccountNumber,
      year,
      month,
      files: selectedFiles,
    });

    if (success) {
      finishClose();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Backdrop click must not close the upload stepper (users often click away from the manual).
        if (reason === "backdropClick") return;
        if (isUploading) {
          onRequestCancelUpload?.();
          return;
        }
        finishClose();
      }}
      fullWidth
      maxWidth="sm"
      /* Centered dialog; scrollable under zoom so actions stay reachable. */
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "center",
          alignItems: "center",
          overflowX: "hidden",
          overflowY: "auto",
        },
      }}
      PaperProps={{
        sx: {
          ...DIALOG_PAPER_SX,
          ...(manualPanelOpen
            ? { mr: { md: OFFLINE_CUR_DIALOG_MR_WITH_MANUAL } }
            : {}),
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "#FFFFFF",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Upload CUR2.0
        </Typography>
        <button
          type="button"
          aria-label="Close upload dialog"
          onClick={handleClose}
          disabled={isUploading}
          style={{
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isUploading ? "not-allowed" : "pointer",
            opacity: isUploading ? 0.6 : 1,
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </Box>
      <DialogContent sx={{ overflowY: "auto", minHeight: 0, px: 2.5, py: 2.5, backgroundColor: "#fff" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {formError ? (
            <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
              {formError}
            </Alert>
          ) : null}
          <Alert severity="info" sx={INFO_ALERT_SX}>
            <Box component="span" sx={{ display: "block" }}>
              • Enter the CUR 2.0 generated account number.
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              • Supports .gz format CUR 2.0 files ONLY.
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              • Supports uploading multiple CUR 2.0 files for the same month.
            </Box>
          </Alert>
          <Autocomplete
            freeSolo
            size="small"
            options={accountNumberOptions}
            inputValue={accountNumber}
            onInputChange={(event, newInputValue) =>
              setAccountNumber((newInputValue ?? "").replace(/\D/g, "").slice(0, 12))
            }
            fullWidth
            sx={OUTLINED_FIELD_SX}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account number"
                placeholder="Type or select an account"
                error={Boolean(normalizedAccountNumber) && !isValidAccountNumber}
                helperText={
                  Boolean(normalizedAccountNumber) && !isValidAccountNumber
                    ? "Account number must be exactly 12 digits."
                    : ""
                }
                sx={OUTLINED_FIELD_SX}
              />
            )}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <FormControl size="small" fullWidth sx={OUTLINED_FIELD_SX}>
              <InputLabel>Month</InputLabel>
              <Select
                value={month}
                label="Month"
                onChange={(e) => setMonth(Number(e.target.value))}
                MenuProps={SELECT_MENU_PROPS}
              >
                {monthNames.map((monthName, index) => (
                  <MenuItem key={monthName} value={index + 1}>
                    {monthName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={OUTLINED_FIELD_SX}>
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) => setYear(Number(e.target.value))}
                MenuProps={SELECT_MENU_PROPS}
              >
                {yearOptions.map((optionYear) => (
                  <MenuItem key={optionYear} value={optionYear}>
                    {optionYear}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <input
              id="offline-cur-file-input"
              type="file"
              multiple
              accept=".gz,application/gzip"
              style={{ display: "none" }}
              disabled={isUploading}
              onChange={handleFileChange}
            />
            <label htmlFor="offline-cur-file-input" style={isUploading ? { pointerEvents: "none" } : undefined}>
              <Button
                component="span"
                variant="contained"
                disableElevation
                disabled={isUploading}
                sx={{
                  ...LAUNCH_BLUE_BTN_SX,
                  height: 40,
                }}
              >
                {selectedFiles.length > 0 ? "Change file(s)" : "Choose file(s)"}
              </Button>
            </label>
            {selectedFiles.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  mt: 1,
                  pl: 2.25,
                  m: 0,
                  maxHeight: 160,
                  overflowY: "auto",
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  listStyle: "disc",
                }}
              >
                {selectedFiles.map((f) => (
                  <Box
                    key={`${f.name}-${f.size}-${f.lastModified}`}
                    component="li"
                    sx={{ wordBreak: "break-all", display: "list-item" }}
                  >
                    {f.name}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: "#64748b" }}>No file(s) selected</Typography>
            )}
          </Box>
          {isUploading && !uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  borderRadius: "8px",
                  height: 8,
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
                }}
              />
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                {uploadProgress?.fileCount > 1
                  ? `Preparing file ${uploadProgress.fileIndex} of ${uploadProgress.fileCount}…`
                  : "Preparing…"}
              </Typography>
              {uploadProgress?.fileCount > 1 && uploadProgress?.currentFileName ? (
                <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", wordBreak: "break-all" }}>
                  {uploadProgress.currentFileName}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {isUploading && uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (uploadProgress.completed / uploadProgress.total) * 100)}
                sx={{
                  borderRadius: "8px",
                  height: 8,
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
                }}
              />
              {uploadProgress?.fileCount > 1 && (
                <Typography sx={{ mt: 0.5, fontSize: "0.7rem", color: "#64748b", wordBreak: "break-all" }}>
                  File {uploadProgress.fileIndex} of {uploadProgress.fileCount}
                  {uploadProgress.currentFileName ? `: ${uploadProgress.currentFileName}` : ""}
                </Typography>
              )}
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                Uploading part {uploadProgress.completed} of {uploadProgress.total}…
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          flexShrink: 0,
          gap: 1,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleClose}
          variant="contained"
          disableElevation
          sx={GREY_CLOSE_BTN_SX}
        >
          {isUploading ? "Cancel upload" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleUploadClick}
          disabled={isUploading || !isFormComplete}
          sx={PRIMARY_BTN_SX}
        >
          {isUploading ? (uploadProgress?.total ? "Uploading…" : "Preparing…") : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={uploadConfirmOpen}
      onClose={() => setUploadConfirmOpen(false)}
      fullWidth
      maxWidth="sm"
      disableRestoreFocus
      sx={{ zIndex: OFFLINE_CUR_CONFIRM_Z_INDEX }}
      PaperProps={{
        sx: DIALOG_PAPER_SX,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 2.5,
          py: 1.75,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.0625rem",
            color: "#FFFFFF",
            lineHeight: 1.3,
          }}
        >
          Confirm upload
        </Typography>
      </Box>
      <DialogContent sx={{ overflowY: "auto", minHeight: 0, px: 2.5, py: 2.5 }}>
        <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.6 }} component="div">
          {selectedFiles.length === 1 ? (
            <>
              This file <strong>{selectedFiles[0].name}</strong> will be uploaded into the system for CUR
              processing for account number : <strong>{normalizedAccountNumber}</strong> and your organization
              : <strong>{organizationDisplayName || "—"}</strong>. Please confirm to proceed.
            </>
          ) : selectedFiles.length > 1 ? (
            <>
              These files:{" "}
              {selectedFiles.map((f, i) => (
                <React.Fragment key={`${f.name}-${f.size}-${f.lastModified}`}>
                  {i > 0 ? ", " : null}
                  <strong>{f.name}</strong>
                </React.Fragment>
              ))}{" "}
              will be uploaded into the system for CUR processing for account number :{" "}
              <strong>{normalizedAccountNumber}</strong> and your organization :{" "}
              <strong>{organizationDisplayName || "—"}</strong>. Please confirm to proceed.
            </>
          ) : null}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          flexShrink: 0,
          gap: 1,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={() => setUploadConfirmOpen(false)}
          variant="contained"
          disableElevation
          sx={GREY_CLOSE_BTN_SX}
        >
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={handleConfirmUpload} sx={PRIMARY_BTN_SX}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    {open && manualPanelOpen && finOpsManualGuideSrc ? (
      <RemediationPreviewPanel
        url={finOpsManualGuideSrc}
        onClose={() => setManualPanelOpen(false)}
        title="CUR 2.0 manual"
      />
    ) : null}
    {open && manualPanelOpen && !finOpsManualGuideSrc
      ? createPortal(
          <Paper
            elevation={8}
            component="aside"
            aria-label="CUR 2.0 manual document preview"
            sx={{
              position: "fixed",
              right: 0,
              top: 0,
              bottom: 0,
              left: { xs: 0, md: "auto" },
              width: {
                xs: "100vw",
                md: `${OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX}px`,
              },
              minWidth: 0,
              maxWidth: "100vw",
              maxHeight: "100dvh",
              zIndex: OFFLINE_CUR_MANUAL_Z_INDEX,
              borderRadius: 0,
              border: `1px solid ${PREVIEW_PANEL_COLORS.border}`,
              borderTop: 0,
              borderBottom: 0,
              borderRight: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              boxSizing: "border-box",
              backgroundColor: PREVIEW_PANEL_COLORS.card,
            }}
          >
            <Box
              component="header"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                minHeight: 44,
                px: 1.5,
                py: 0.75,
                backgroundColor: PREVIEW_PANEL_COLORS.borderLight,
                borderBottom: `1px solid ${PREVIEW_PANEL_COLORS.border}`,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: PREVIEW_PANEL_COLORS.text,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title="CUR 2.0 manual"
              >
                CUR 2.0 manual
              </Typography>
              <IconButton
                onClick={() => setManualPanelOpen(false)}
                size="small"
                aria-label="Close CUR 2.0 manual"
                sx={{
                  color: PREVIEW_PANEL_COLORS.textMuted,
                  "&:hover": { color: PREVIEW_PANEL_COLORS.text },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {finOpsManualGuideError ? (
              <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
                <Alert
                  severity="error"
                  sx={{
                    fontSize: "0.75rem",
                    py: 0,
                    "& .MuiAlert-message": { padding: 0.75 },
                  }}
                >
                  {finOpsManualGuideError}
                </Alert>
              </Box>
            ) : null}
            {finOpsManualGuideLoading && !finOpsManualGuideError ? (
              <Box
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: PREVIEW_PANEL_COLORS.background,
                }}
              >
                <CircularProgress size={40} />
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: "0.8125rem",
                    color: PREVIEW_PANEL_COLORS.textMuted,
                  }}
                >
                  Loading manual preview…
                </Typography>
              </Box>
            ) : null}
            {!finOpsManualGuideError && !finOpsManualGuideLoading ? (
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: PREVIEW_PANEL_COLORS.background,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: PREVIEW_PANEL_COLORS.textMuted,
                  }}
                >
                  No document URL returned.
                </Typography>
              </Box>
            ) : null}
          </Paper>,
          document.body
        )
      : null}
    </>
  );
};

export default UploadOfflineCurModal;
